import {
  IStepValuePersonalInfo,
  TWizardStepComponentEditable,
  WIZARD_STUDENT_STEPS_PATHS,
  TStepPayload,
  TStepErrors,
  TWizardStepComponentEditableView,
  IWizardStudentState
} from "../../../interfaces";
import { DatePicker, Input, Form, Select } from "antd";
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState
} from "react";
import dayjs from "dayjs";
import {
  formatAsUTCToServerDate,
  formatDateToShort,
  parseServerDate
} from "../../../../../utils/time";
import { useTranslation } from "react-i18next";
import { useContextHospi } from "../../../../../context/ContextHospi";
import { UserAPI } from "../../../../../API/userApi";
import { getChangedFields, getStepErrorsFromResponse } from "../../../utils";
import { getCountries, getPhoneCodes } from "../../../../../utils/fetchMapInfo";
import { TLang } from "../../../../../assets/data/isoNameLanguage";
import styled from "styled-components/macro";
import { GenderValues } from "../../../../../interfaces/interfaces";
import { StepViewOnlyValue } from "../../styles";
import { RadioGroup } from "../../../components";
import { DATE_PICKER_DATE_FORMAT } from "../../../../../constants";

const statePath = WIZARD_STUDENT_STEPS_PATHS.PersonalInfo;

export const WizardStepPersonalInfo: TWizardStepComponentEditable<IWizardStudentState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<TStepPayload<IStepValuePersonalInfo>>(
      state[statePath] ?? {}
    );
    const [errors, setErrors] =
      useState<TStepErrors<IStepValuePersonalInfo>>(null);

    const { currentUserRole } = useContextHospi();
    const { i18n } = useTranslation(currentUserRole);

    useImperativeHandle(ref, () => ({
      save: () => {
        setErrors(null);
        return UserAPI.wizardStudentSavePersonalInfo({
          ...values,
          default_language: i18n.language,
          user_type: currentUserRole
        })
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      },
      savePartially: () => {
        setErrors(null);
        return UserAPI.wizardStudentSavePersonalInfoPartially(
          getChangedFields(state[statePath], values)
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      }
    }));

    return (
      <WizardStepPersonalInfoView
        values={values}
        setValues={setValues}
        errors={errors}
        readonly={readonly}
        viewonly={viewonly}
      />
    );
  });

const GenderFormItem = styled(Form.Item)`
  grid-column-start: span 2;
`;

const Root = styled(Form)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;

    ${GenderFormItem} {
      grid-column-start: auto;
    }
  }
`;

const CountryCodeSelect = styled(Select)`
  .ant-select-selection-item {
    width: 100px;
  }
`;

export const WizardStepPersonalInfoView: TWizardStepComponentEditableView<IStepValuePersonalInfo> =
  React.memo(({ values, setValues, errors, readonly, viewonly }) => {
    const translationUserRole = "student";
    const { t, i18n } = useTranslation(translationUserRole);
    const phoneCodes = useMemo(
      () =>
        getPhoneCodes({
          showCountryNames: true,
          language: i18n.language.toUpperCase() as TLang
        }),
      [i18n.language]
    );
    const countries = useMemo(
      () => getCountries(i18n.language.toUpperCase() as TLang),
      [i18n.language]
    );

    const inputSize = "large";

    const genderOptions = GenderValues.map((o) => ({
      value: o,
      label: t(
        `${translationUserRole}Profile.aboutMeInfo.form.gender.options.${o}`
      )
    }));

    return (
      <Root layout="vertical">
        <Form.Item
          label={t(
            `${translationUserRole}Profile.personalDetails.form.firstName.title`
          )}
          validateStatus={errors?.first_name ? "error" : ""}
          help={
            errors?.first_name
              ? t(
                  `${translationUserRole}Profile.personalDetails.form.generic.error`
                )
              : ""
          }
        >
          {viewonly ? (
            <StepViewOnlyValue>{values.first_name}</StepViewOnlyValue>
          ) : (
            <Input
              size={inputSize}
              disabled={readonly}
              value={values.first_name ?? undefined}
              placeholder={t(
                `${translationUserRole}Profile.personalDetails.form.firstName.placeholder`
              )}
              onChange={(e) => {
                setValues({
                  ...values,
                  first_name: e.target.value
                });
              }}
            />
          )}
        </Form.Item>
        <Form.Item
          label={t(
            `${translationUserRole}Profile.personalDetails.form.lastName.title`
          )}
          validateStatus={errors?.last_name ? "error" : ""}
          help={
            errors?.last_name
              ? t(
                  `${translationUserRole}Profile.personalDetails.form.generic.error`
                )
              : ""
          }
        >
          {viewonly ? (
            <StepViewOnlyValue>{values.last_name}</StepViewOnlyValue>
          ) : (
            <Input
              size={inputSize}
              disabled={readonly}
              value={values.last_name ?? undefined}
              placeholder={t(
                `${translationUserRole}Profile.personalDetails.form.lastName.placeholder`
              )}
              onChange={(e) => {
                setValues({
                  ...values,
                  last_name: e.target.value
                });
              }}
            />
          )}
        </Form.Item>
        <GenderFormItem
          label={t(
            `${translationUserRole}Profile.aboutMeInfo.form.gender.title`
          )}
          validateStatus={errors?.gender ? "error" : ""}
          help={
            errors?.gender
              ? t(`${translationUserRole}Profile.aboutMeInfo.form.gender.error`)
              : ""
          }
        >
          {viewonly ? (
            <StepViewOnlyValue>
              {genderOptions.find((v) => v.value === values.gender)?.label}
            </StepViewOnlyValue>
          ) : (
            <RadioGroup
              value={values.gender ?? undefined}
              options={genderOptions}
              disabled={readonly}
              onChange={(val) => {
                setValues({
                  ...values,
                  gender: val.target.value
                });
              }}
            />
          )}
        </GenderFormItem>
        <Form.Item
          label={t(
            `${translationUserRole}Profile.personalDetails.form.birthDate.title`
          )}
          validateStatus={errors?.birth_date ? "error" : ""}
          help={
            errors?.birth_date
              ? t(
                  `${translationUserRole}Profile.personalDetails.form.generic.error`
                )
              : ""
          }
        >
          {viewonly ? (
            <StepViewOnlyValue>
              {values.birth_date
                ? formatDateToShort(parseServerDate(values.birth_date))
                : ""}
            </StepViewOnlyValue>
          ) : (
            <DatePicker
              size={inputSize}
              disabled={readonly}
              value={
                values.birth_date
                  ? dayjs(parseServerDate(values.birth_date))
                  : undefined
              }
              style={{
                width: "100%"
              }}
              placeholder={t(
                `${translationUserRole}Profile.personalDetails.form.birthDate.placeholder`
              )}
              onChange={(e) => {
                setValues({
                  ...values,
                  birth_date: e ? formatAsUTCToServerDate(e.toDate()) : null
                });
              }}
              format={DATE_PICKER_DATE_FORMAT}
              disabledDate={(date) => {
                return (
                  date.toDate().getTime() >=
                  dayjs(new Date()).startOf("day").toDate().getTime()
                );
              }}
            />
          )}
        </Form.Item>
        <Form.Item
          label={t(
            `${translationUserRole}Profile.personalDetails.form.birthCountry.title`
          )}
          validateStatus={errors?.birth_country ? "error" : ""}
          help={
            errors?.birth_country
              ? t(
                  `${translationUserRole}Profile.personalDetails.form.generic.error`
                )
              : ""
          }
        >
          {viewonly ? (
            <StepViewOnlyValue>
              {countries.find((c) => c.value === values.birth_country)?.label ??
                ""}
            </StepViewOnlyValue>
          ) : (
            <Select
              size={inputSize}
              disabled={readonly}
              value={values.birth_country ?? undefined}
              options={countries}
              showSearch={true}
              placeholder={t(
                `${translationUserRole}Profile.personalDetails.form.birthCountry.placeholder`
              )}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(val) => {
                setValues({
                  ...values,
                  birth_country: val
                });
              }}
            />
          )}
        </Form.Item>
        <Form.Item
          label={t(
            `${translationUserRole}Profile.personalDetails.form.countryCode.title`
          )}
          validateStatus={errors?.phone_code ? "error" : ""}
          help={
            errors?.phone_code
              ? t(
                  `${translationUserRole}Profile.personalDetails.form.generic.error`
                )
              : ""
          }
        >
          {viewonly ? (
            <StepViewOnlyValue>
              {phoneCodes.find((c) => c.value === values.phone_code)?.label ??
                ""}
            </StepViewOnlyValue>
          ) : (
            <CountryCodeSelect
              size={inputSize}
              disabled={readonly}
              value={values.phone_code}
              options={phoneCodes}
              showSearch={true}
              placeholder={t(
                `${translationUserRole}Profile.personalDetails.form.countryCode.placeholder`
              )}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(val) => {
                setValues({
                  ...values,
                  phone_code: val as string | null | undefined
                });
              }}
            />
          )}
        </Form.Item>
        <Form.Item
          label={t(
            `${translationUserRole}Profile.personalDetails.form.phone.title`
          )}
          validateStatus={errors?.phone_number ? "error" : ""}
          help={
            errors?.phone_number
              ? t(
                  `${translationUserRole}Profile.personalDetails.form.generic.error`
                )
              : ""
          }
        >
          {viewonly ? (
            <StepViewOnlyValue>{values.phone_number ?? ""}</StepViewOnlyValue>
          ) : (
            <Input
              size={inputSize}
              disabled={readonly}
              value={values.phone_number ?? undefined}
              placeholder={t(
                `${translationUserRole}Profile.personalDetails.form.phone.placeholder`
              )}
              onChange={(e) => {
                if (
                  e.target.value === "" ||
                  /^[\d- ()]+$/.test(e.target.value)
                ) {
                  setValues({
                    ...values,
                    phone_number: e.target.value
                  });
                }
              }}
            />
          )}
        </Form.Item>
      </Root>
    );
  });
